/** @jsxImportSource @emotion/react */
import { Box, Button, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { AuthConfirmLogout } from '@app/components/Auth/AuthConfirmLogout'
import NewChangeClient from '@app/layout/ChangeClient'
import {
  visibleSidebarFooterAtom,
  sidebarDispatchAtom,
  type UserOption,
  sidebarFooterLoadedAtom,
  sidebarFullyLoadedAtom,
} from '@app/state/sidebarAtom'
// TODO: Move this to the newww sidebarAtom
import { SidebarAtom } from '@app/state/layoutAtom'
import { showClientTsnCsModalAtom, showFeedbackModalAtom } from '@app/state/modalAtom'
import { motion } from 'framer-motion'
import { useAtomValue, useSetAtom, useAtom } from 'jotai'
import { useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { sidebarVariants } from './Sidebar'
import {
  appVersionLabel,
  feedbackLink,
  sidebarButtonsContainer,
  sidebarButtonsInnerContainer,
  sidebarFooter,
} from './Sidebar.style'
import NewSidebarMenuItem from './SidebarMenuItem'
import { userAtom } from '@app/hooks/useUserProfile'
import {
  isAuthenticatedAtom,
  isLoadingAuthStateAtom,
  showLogoutDialogAtom,
} from '@app/state/authAtom'
import { SidebarSkeleton } from './Sidebar.skeleton'
import { useAuth } from '@app/Auth/hooks/useAuth'

export const SidebarFooter = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { initiateLogin, initiateLogout } = useAuth()

  const isOpen = useAtomValue(SidebarAtom)
  const sidebarDispatch = useSetAtom(sidebarDispatchAtom)
  const isAuthenticated = useAtomValue(isAuthenticatedAtom)
  const setIsLoadingAuth = useSetAtom(isLoadingAuthStateAtom)
  const setShowFeedbackModal = useSetAtom(showFeedbackModalAtom)
  const sidebarFullyLoaded = useAtomValue(sidebarFullyLoadedAtom)
  const setSidebarFooterLoaded = useSetAtom(sidebarFooterLoadedAtom)
  const setShowClientTsnCsModal = useSetAtom(showClientTsnCsModalAtom)
  const isShowClientTsnCsModalOpen = useAtomValue(showClientTsnCsModalAtom)
  const [showLogoutDialog, setShowLogoutDialog] = useAtom(showLogoutDialogAtom)
  const visibleMenuItems: UserOption[] = useAtomValue(visibleSidebarFooterAtom)

  const { data: userProfile, isSuccess: userProfileLoaded } = useAtomValue(userAtom)

  useEffect(() => {
    if (userProfile && userProfileLoaded) {
      setSidebarFooterLoaded(true)
    }

    if (userProfile && userProfile.accepted_tsncs === false && !isShowClientTsnCsModalOpen) {
      setShowClientTsnCsModal(true)
    } else if (userProfile && userProfile.accepted_tsncs === true && isShowClientTsnCsModalOpen) {
      setShowClientTsnCsModal(false)
    }
  }, [
    userProfile,
    isShowClientTsnCsModalOpen,
    setShowClientTsnCsModal,
    userProfileLoaded,
    setSidebarFooterLoaded,
  ])

  const handleLogout = useCallback(() => {
    setShowLogoutDialog(false)
    setIsLoadingAuth(true)
    initiateLogout()
  }, [setShowLogoutDialog, initiateLogout, setIsLoadingAuth])

  return (
    <nav aria-label="User options">
      <motion.div
        variants={sidebarVariants}
        animate={isOpen ? 'open' : 'closed'}
        transition={{ duration: 0.2 }}
        css={sidebarButtonsContainer(theme)}
      >
        {isAuthenticated && (
          <>
            <SidebarSkeleton loading={!sidebarFullyLoaded} rows={4} rowHeight={37} />
            <Box css={sidebarButtonsInnerContainer(theme, sidebarFullyLoaded)}>
              {visibleMenuItems.map((item) => {
                return (
                  <NewSidebarMenuItem
                    key={item.id}
                    icon={item.icon}
                    label={item.label}
                    active={false}
                    onClick={
                      item.navigateTo
                        ? () => navigate(item.navigateTo!)
                        : () => sidebarDispatch(item.onClick)
                    }
                    displayLabel={isOpen}
                    size="medium"
                  />
                )
              })}
            </Box>
          </>
        )}
        {!isAuthenticated && (
          <NewSidebarMenuItem
            icon={'SignIn'}
            label={'Sign In'}
            active={false}
            onClick={() => {
              initiateLogin()
            }}
            displayLabel={isOpen}
            size="medium"
          />
        )}

        <Box css={sidebarFooter()}>
          <Button
            variant="text"
            sx={{ textDecoration: 'none', transition: 'all 0.2s', p: 0, ml: isOpen ? 0 : '-10px' }}
            onClick={() => setShowFeedbackModal(true)}
          >
            <Typography css={feedbackLink(theme)}>{isOpen ? 'Submit' : ''} Feedback</Typography>
          </Button>
          <Typography css={appVersionLabel(theme)}>v{APP_VERSION}</Typography>
        </Box>

        <NewChangeClient />
      </motion.div>
      <AuthConfirmLogout
        open={showLogoutDialog}
        onClose={() => setShowLogoutDialog(false)}
        onConfirm={handleLogout}
      />
    </nav>
  )
}

export default SidebarFooter
